// src/components/ObjectSlider.jsx
import React from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import { normalizeMediaUrl } from '../api/base';

const SliderWrap = styled.div`
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: var(--shadow);
  background: #000;
  margin-bottom: 24px;
  
  .swiper {
    width: 100%;
    height: 404px; /* Фиксированная высота как в DistrictSlider */
  }
  
  .swiper-slide {
    position: relative;
    overflow: hidden;
    width: auto !important; /* Автоматическая ширина */
    height: 404px;
    cursor: default; /* Обычный курсор */
    
    img {
      height: 100%;
      width: auto;
      object-fit: cover;
      display: block;
      border-right: 2px solid rgba(255, 255, 255, 0.3); /* Разделитель между фотками */
    }
    
    /* Убираем разделитель у последнего слайда */
    &:last-child img {
      border-right: none;
    }
  }
  
  .swiper-button-next,
  .swiper-button-prev {
    color: var(--primary);
    background: rgba(255, 255, 255, 0.9);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    margin-top: -22px;
    
    &:after {
      font-size: 18px;
      font-weight: 700;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 1);
    }
    
    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
      margin-top: -18px;
      
      &:after {
        font-size: 14px;
      }
    }
  }
`;

const PropertyInfo = styled.div`
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 60px;
  z-index: 2;
  
  h1 {
    margin: 0 0 8px;
    font-size: clamp(24px, 4vw, 36px);
    font-weight: 700;
    line-height: 1.2;
    color: white;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
  }
  
  .price {
    margin: 0 0 8px;
    font-size: clamp(18px, 3vw, 24px);
    font-weight: 800;
    color: var(--gold, #d4af37);
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
  }
  
  .details {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    font-size: clamp(14px, 2.5vw, 18px);
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.9);
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.7);
    max-width: 600px;
  }
  
  .detail-item {
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }
  
  @media (max-width: 768px) {
    left: 16px;
    right: 16px;
    bottom: 50px;
    
    .details {
      gap: 12px;
    }
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.6) 100%
  );
  z-index: 1;
  pointer-events: none;
`;

// Lightbox
const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.95);
  display: grid;
  place-items: center;
  backdrop-filter: blur(4px);
`;

const LBInner = styled.div`
  width: min(1400px, 96vw);
  height: min(92vh, 900px);
  position: relative;
`;

const LBImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const LBClose = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const LBArrow = styled.button`
  position: absolute;
  top: 50%;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 28px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transform: translateY(-50%);
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;
  
  ${props => props.$left ? 'left: 30px;' : 'right: 30px;'}
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: translateY(-50%) scale(1.1);
  }
  
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 20px;
    ${props => props.$left ? 'left: 20px;' : 'right: 20px;'}
  }
`;

const LBCounter = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

export default function ObjectSlider({ 
  property, 
  images = [], 
  onImageClick,
  showPropertyInfo = true,
  formatPrice 
}) {
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxIndex, setLightboxIndex] = React.useState(0);

  const sliderImages = React.useMemo(() => {
    const allImages = [];
    
    // Добавляем изображения из галереи
    if (Array.isArray(images) && images.length > 0) {
      images.forEach(img => {
        const imageUrl = img?.url || img?.thumbnail_url || img;
        if (imageUrl) {
          allImages.push({
            url: normalizeMediaUrl(imageUrl),
            title: String(img?.title || img?.alt_text || property?.title || 'Объект'),
            description: img?.description || img?.caption || ''
          });
        }
      });
    }
    
    // Если нет изображений из галереи, добавляем основное изображение объекта
    if (allImages.length === 0 && property?.cover_url) {
      allImages.push({
        url: normalizeMediaUrl(property.cover_url),
        title: String(property?.title || 'Объект'),
        description: property?.description || ''
      });
    }
    
    // Если совсем нет изображений, добавляем заглушку
    if (!allImages.length) {
      allImages.push({
        url: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&h=900&fit=crop&q=85',
        title: String(property?.title || 'Объект недвижимости'),
        description: property?.description || ''
      });
    }
    
    return allImages;
  }, [property, images]);

  // Блокировка скролла при лайтбоксе
  React.useEffect(() => {
    if (!lightboxOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxOpen(false);
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex(prev => Math.min(sliderImages.length - 1, prev + 1));
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxOpen, sliderImages.length]);

  const openLightbox = (imageIndex) => {
    setLightboxIndex(imageIndex);
    setLightboxOpen(true);
  };

  const handleImageClick = (imageIndex) => {
    if (onImageClick) {
      onImageClick(imageIndex);
    } else {
      openLightbox(imageIndex);
    }
  };

  return (
    <SliderWrap>
      <Swiper
        modules={[Navigation, FreeMode]}
        navigation={sliderImages.length > 1}
        spaceBetween={0}
        slidesPerView="auto" /* Автоматическое количество слайдов */
        freeMode={true} /* Свободное листание */
      >
        {sliderImages.map((image, index) => (
          <SwiperSlide key={index}>
            <img
              src={image.url}
              alt={image.title}
              loading={index === 0 ? 'eager' : 'lazy'}
              onClick={() => handleImageClick(index)}
            />
            {/* Показываем информацию об объекте только на первом слайде */}
            {index === 0 && showPropertyInfo && (
              <>
                <Overlay />
                <PropertyInfo>
                  <h1>{property?.title || 'Объект недвижимости'}</h1>
                  {formatPrice && (
                    <div 
                      className="price"
                      dangerouslySetInnerHTML={{ __html: formatPrice(property) }}
                    />
                  )}
                  <div className="details">
                    {property?.bedrooms && (
                      <div className="detail-item">
                        🛏️ {property.bedrooms} спален
                      </div>
                    )}
                    {property?.area_total && (
                      <div className="detail-item">
                        📐 {property.area_total} м²
                      </div>
                    )}
                    {property?.district?.name && (
                      <div className="detail-item">
                        📍 {property.district.name}
                      </div>
                    )}
                  </div>
                </PropertyInfo>
              </>
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Лайтбокс */}
      {lightboxOpen && (
        <Lightbox onClick={() => setLightboxOpen(false)}>
          <LBInner onClick={(e) => e.stopPropagation()}>
            <LBClose onClick={() => setLightboxOpen(false)}>×</LBClose>
            
            <LBCounter>
              {lightboxIndex + 1} / {sliderImages.length}
            </LBCounter>
            
            {lightboxIndex > 0 && (
              <LBArrow
                $left
                onClick={() => setLightboxIndex(prev => Math.max(0, prev - 1))}
              >
                ‹
              </LBArrow>
            )}
            
            {lightboxIndex < sliderImages.length - 1 && (
              <LBArrow
                onClick={() => setLightboxIndex(prev => Math.min(sliderImages.length - 1, prev + 1))}
              >
                ›
              </LBArrow>
            )}
            
            <LBImg
              src={sliderImages[lightboxIndex].url}
              alt={sliderImages[lightboxIndex].title}
            />
          </LBInner>
        </Lightbox>
      )}
    </SliderWrap>
  );
}